/**
 * Lists module — shared reference data selected from by other modules.
 * Mirrors the entities in backend/entity/{Customer,Vendor,Branch}.js.
 */

export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'VIC', 'WA'] as const;
export type AuState = typeof AU_STATES[number];

export const VENDOR_CATEGORIES = [
    'Uniform & PPE',
    'Automotive',
    'Tools & Hardware',
    'Equipment',
    'IT Equipment',
    'Other',
] as const;
export type VendorCategory = typeof VENDOR_CATEGORIES[number];

export const VENDOR_MODULES = [
    'Assets',
    'Uniform & PPE',
    'Business Systems',
    'Incoming Stock',
] as const;
export type VendorModule = typeof VENDOR_MODULES[number];

export interface ListTag {
    id: number;
    name: string;
}

export interface Customer {
    id: number;
    legalName: string;
    tradingName: string;
    abnNumber: string | null;
    address: string | null;
    state: AuState;
    website: string | null;
    contactName: string | null;
    phone: string | null;
    email: string | null;
    facebook: string | null;
    linkedin: string | null;
    categories: ListTag[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/** Minimal vendor shape returned to the People → Subcontractor picker. */
export interface SubcontractorVendorOption {
    id: number;
    legalName: string;
    tradingName: string;
    isActive: boolean;
}

export interface Vendor {
    id: number;
    legalName: string;
    tradingName: string;
    abnNumber: string | null;
    website: string | null;
    contactName: string | null;
    phone: string | null;
    email: string | null;
    /** Retained from the former inventory_suppliers table; not part of the Lists spec. */
    address: string | null;
    category: VendorCategory | null;
    tags: ListTag[];
    showInModules: VendorModule[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface BranchContact {
    id: number;
    name: string;
    email?: string;
    jobRole?: string | null;
    /** Only populated by /api/users?all_users=true — person pickers use it. */
    department?: { id: number; departmentName: string } | null;
}

export interface Branch {
    id: number;
    name: string;
    address: string;
    mainContactPerson: BranchContact | null;
    description: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface ProjectCustomerRef {
    id: number;
    legalName: string;
    tradingName: string;
    isActive: boolean;
}

export interface ProjectBranchRef {
    id: number;
    name: string;
    isActive: boolean;
}

export interface ProjectYardRef {
    id: number;
    yardName: string;
    yardCode: string;
    status: 'open' | 'closed';
}

export interface Project {
    id: number;
    projectName: string;
    projectNumber: string;
    siteAddress: string;
    customer: ProjectCustomerRef | null;
    projectManager: BranchContact | null;
    branch: ProjectBranchRef | null;
    /** Required since the Yards list shipped — FK, NOT NULL. */
    yard: ProjectYardRef | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/** Payloads accept either an existing tag id or a new tag name, created on the fly. */
export type TagInput = (number | string)[];

export interface CustomerPayload {
    legalName: string;
    tradingName: string;
    abnNumber?: string | null;
    address?: string | null;
    state: AuState | '';
    website?: string | null;
    contactName?: string | null;
    phone?: string | null;
    email?: string | null;
    facebook?: string | null;
    linkedin?: string | null;
    categories?: TagInput;
}

export interface VendorPayload {
    legalName: string;
    tradingName: string;
    abnNumber?: string | null;
    website?: string | null;
    contactName?: string | null;
    phone?: string | null;
    email?: string | null;
    category?: VendorCategory | null;
    tags?: TagInput;
    showInModules?: VendorModule[];
}

export interface BranchPayload {
    name: string;
    address: string;
    mainContactPersonId: number | null;
    description?: string | null;
}

export interface ProjectPayload {
    projectName: string;
    projectNumber: string;
    siteAddress: string;
    customerId: number | null;
    projectManagerId: number | null;
    branchId: number | null;
    /** Required. Only OPEN yards are selectable — closed yards are read-only. */
    yardId: number | null;
}

export type ListStatusFilter = 'active' | 'inactive';
